/**
 * Stockage Editor — Graph generators per plant
 *
 * Generates React Flow nodes & edges for:
 * - 2 plants: CBER (Bercy), CAuber (Aubervilliers)
 * - Network storages (standalone entities with departure/return/pumps)
 */
import type { Edge, Node } from '@xyflow/react';
import type { IBaseData } from '@shared/production-editor';
import type { CondenserCoolingType } from './types';

const EDGE_GREY_DASHED = {
    stroke: 'var(--nj-core-color-grey-400)',
    strokeWidth: 1,
    strokeDasharray: '4 4',
};

export type PlantId = 'cber' | 'cauber';
export type StorageNetworkId = string;

export interface GroupConfigs {
    [groupId: string]: CondenserCoolingType;
}

/**
 * Build graph for a plant (centrale)
 */
export function buildGraphForPlant(
    plantId: PlantId,
    groupConfigs?: GroupConfigs
): { nodes: Node<IBaseData>[]; edges: Edge[] } {
    switch (plantId) {
        case 'cber':
            return buildCBER(groupConfigs);
        case 'cauber':
            return buildCAuber(groupConfigs);
        default:
            return buildCBER(groupConfigs);
    }
}

/**
 * Build graph for a network storage (standalone)
 */
export function buildGraphForStorageNetwork(
    storageId: StorageNetworkId,
    storageName: string,
    returnNode: string,
    supplyNode: string
): { nodes: Node<IBaseData>[]; edges: Edge[] } {
    const nodes: Node<IBaseData>[] = [
        // Return
        {
            id: `${storageId}-return`,
            type: 'asset',
            position: { x: 50, y: 150 },
            data: {
                entityName: 'Return',
                entityLabel: 'Retour stockage réseau',
                label: 'Retour stockage réseau',
                nodeId: returnNode,
            },
            width: 140,
            height: 120,
        },
        // Storage tank (central node)
        {
            id: `${storageId}-tank`,
            type: 'asset',
            position: { x: 300, y: 130 },
            data: {
                entityName: 'StorageTank',
                entityLabel: 'Stockage sur réseau',
                label: storageName,
            },
            width: 160,
            height: 160,
        },
        // Pump group
        {
            id: `${storageId}-pumps`,
            type: 'group',
            position: { x: 550, y: 80 },
            data: {
                entityName: 'Group',
                entityLabel: 'Groupe de pompe stockage réseau',
                label: 'Groupe de pompe stockage réseau',
            },
            width: 240,
            height: 260,
            style: {
                backgroundColor: 'white',
                border: '1px solid var(--nj-core-color-grey-300)',
                borderRadius: '8px',
            },
        },
        // Pump 1 (inside group)
        {
            id: `${storageId}-pump-1`,
            type: 'asset',
            position: { x: 570, y: 110 },
            data: {
                entityName: 'VariableSpeedPump',
                entityLabel: 'Pompe',
                label: 'Pompe 1',
            },
            parentId: `${storageId}-pumps`,
            extent: 'parent' as const,
            width: 180,
            height: 120,
        },
        // Departure
        {
            id: `${storageId}-departure`,
            type: 'asset',
            position: { x: 850, y: 150 },
            data: {
                entityName: 'Departure',
                entityLabel: 'Départ stockage réseau',
                label: 'Départ stockage réseau',
                nodeId: supplyNode,
            },
            width: 140,
            height: 120,
        },
    ];

    const edges: Edge[] = [
        // Return → Tank (cyan flow)
        {
            id: `${storageId}-e1`,
            source: `${storageId}-return`,
            target: `${storageId}-tank`,
            type: 'animatedFlowCyan',
            sourceHandle: 'right',
            targetHandle: 'left',
        },
        // Tank → Pumps (blue flow)
        {
            id: `${storageId}-e2`,
            source: `${storageId}-tank`,
            target: `${storageId}-pumps`,
            type: 'animatedFlow',
            sourceHandle: 'right',
            targetHandle: 'left',
        },
        // Pumps → Departure (blue flow)
        {
            id: `${storageId}-e3`,
            source: `${storageId}-pumps`,
            target: `${storageId}-departure`,
            type: 'animatedFlow',
            sourceHandle: 's-right',
            targetHandle: 'left',
        },
    ];

    return { nodes, edges };
}

// ── CBER (Bercy) ────────────────────────────────────────────────────────

function buildCBER(_groupConfigs?: GroupConfigs): { nodes: Node<IBaseData>[]; edges: Edge[] } {
    const nodes: Node<IBaseData>[] = [
        // Power contract
        {
            id: 'power-bercy',
            type: 'contract',
            position: { x: 50, y: 20 },
            data: {
                entityName: 'PowerContract',
                entityLabel: "Contrat d'électricité",
                label: 'Contrat élec BERCY',
            },
            width: 165,
            height: 130,
        },
        // Cooling system group
        {
            id: 'cooling-bercy',
            type: 'group',
            position: { x: 50, y: 190 },
            data: {
                entityName: 'WaterCooling',
                entityLabel: 'Système de refroidissement',
                label: 'Système de refroidissement BERCY',
                linkedProductionLines: ['group-line1', 'group-line2', 'group-line3'],
                condenserInletTempMin: 288.15,
                condenserOutletTempMax: 303.15,
            },
            width: 320,
            height: 150,
            style: {
                backgroundColor: 'white',
                border: '1px solid var(--nj-core-color-grey-300)',
                borderRadius: '8px',
            },
        },
        // TAR 1 (inside cooling system group)
        {
            id: 'tar-bercy-1',
            type: 'asset',
            position: { x: 70, y: 220 },
            data: {
                entityName: 'CoolingTower',
                entityLabel: 'TAR',
                label: 'TAR 1',
            },
            parentId: 'cooling-bercy',
            extent: 'parent' as const,
            width: 130,
            height: 90,
        },
        // TAR 2
        {
            id: 'tar-bercy-2',
            type: 'asset',
            position: { x: 210, y: 220 },
            data: {
                entityName: 'CoolingTower',
                entityLabel: 'TAR',
                label: 'TAR 2',
            },
            parentId: 'cooling-bercy',
            extent: 'parent' as const,
            width: 130,
            height: 90,
        },
        // Return
        {
            id: 'return-bercy',
            type: 'asset',
            position: { x: 50, y: 380 },
            data: {
                entityName: 'Return',
                entityLabel: 'Retour',
                label: 'BERCY Retour',
            },
            width: 140,
            height: 120,
        },
        // Production line 1 (group)
        {
            id: 'group-line1',
            type: 'group',
            position: { x: 300, y: 50 },
            data: {
                entityName: 'Group',
                entityLabel: 'Ligne de production',
                label: 'Ligne 1 BERCY',
            },
            width: 380,
            height: 180,
            style: {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                border: '1px solid rgba(25, 118, 210, 0.3)',
                borderRadius: '8px',
            },
        },
        // GF1 (inside line 1)
        {
            id: 'chiller-gf1',
            type: 'asset',
            position: { x: 320, y: 80 },
            data: {
                entityName: 'Chiller',
                entityLabel: 'Groupe froid',
                label: 'GF1',
            },
            parentId: 'group-line1',
            extent: 'parent' as const,
            width: 160,
            height: 130,
        },
        // GF2
        {
            id: 'chiller-gf2',
            type: 'asset',
            position: { x: 500, y: 80 },
            data: {
                entityName: 'Chiller',
                entityLabel: 'Groupe froid',
                label: 'GF2',
            },
            parentId: 'group-line1',
            extent: 'parent' as const,
            width: 160,
            height: 130,
        },
        // Production line 2
        {
            id: 'group-line2',
            type: 'group',
            position: { x: 300, y: 260 },
            data: {
                entityName: 'Group',
                entityLabel: 'Ligne de production',
                label: 'Ligne 2 BERCY',
            },
            width: 200,
            height: 180,
            style: {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                border: '1px solid rgba(25, 118, 210, 0.3)',
                borderRadius: '8px',
            },
        },
        // GF3
        {
            id: 'chiller-gf3',
            type: 'asset',
            position: { x: 320, y: 290 },
            data: {
                entityName: 'Chiller',
                entityLabel: 'Groupe froid',
                label: 'GF3',
            },
            parentId: 'group-line2',
            extent: 'parent' as const,
            width: 160,
            height: 130,
        },
        // Pump group
        {
            id: 'pumps-bercy',
            type: 'group',
            position: { x: 750, y: 50 },
            data: {
                entityName: 'Group',
                entityLabel: 'Groupe de pompes',
                label: 'Groupe de pompes BERCY',
            },
            width: 240,
            height: 390,
            style: {
                backgroundColor: 'white',
                border: '1px solid var(--nj-core-color-grey-300)',
                borderRadius: '8px',
            },
        },
        // Pumps (inside group)
        {
            id: 'pump-1',
            type: 'asset',
            position: { x: 770, y: 80 },
            data: {
                entityName: 'VariableSpeedPump',
                entityLabel: 'Pompe',
                label: 'Pompe 1',
            },
            parentId: 'pumps-bercy',
            extent: 'parent' as const,
            width: 180,
            height: 80,
        },
        {
            id: 'pump-2',
            type: 'asset',
            position: { x: 770, y: 180 },
            data: {
                entityName: 'VariableSpeedPump',
                entityLabel: 'Pompe',
                label: 'Pompe 2',
            },
            parentId: 'pumps-bercy',
            extent: 'parent' as const,
            width: 180,
            height: 80,
        },
        {
            id: 'pump-3',
            type: 'asset',
            position: { x: 770, y: 280 },
            data: {
                entityName: 'VariableSpeedPump',
                entityLabel: 'Pompe',
                label: 'Pompe 3',
            },
            parentId: 'pumps-bercy',
            extent: 'parent' as const,
            width: 180,
            height: 80,
        },
        {
            id: 'pump-4',
            type: 'asset',
            position: { x: 770, y: 380 },
            data: {
                entityName: 'VariableSpeedPump',
                entityLabel: 'Pompe',
                label: 'Pompe 4',
            },
            parentId: 'pumps-bercy',
            extent: 'parent' as const,
            width: 180,
            height: 80,
        },
        // Departure
        {
            id: 'departure-bercy',
            type: 'asset',
            position: { x: 1050, y: 200 },
            data: {
                entityName: 'Departure',
                entityLabel: 'Départ',
                label: 'BERCY Départ',
            },
            width: 140,
            height: 120,
        },
    ];

    const edges: Edge[] = [
        // Power → Cooling system (dashed)
        {
            id: 'e-power-cooling',
            source: 'power-bercy',
            target: 'cooling-bercy',
            style: EDGE_GREY_DASHED,
        },
        // Cooling system → Lines (orange flow from bottom)
        {
            id: 'e-cooling-line1',
            source: 'cooling-bercy',
            target: 'group-line1',
            type: 'animatedFlowOrange',
            sourceHandle: 's-bottom',
            targetHandle: 'top',
        },
        {
            id: 'e-cooling-line2',
            source: 'cooling-bercy',
            target: 'group-line2',
            type: 'animatedFlowOrange',
            sourceHandle: 's-bottom',
            targetHandle: 'top',
        },
        // Return → Lines (cyan flow from right to left)
        {
            id: 'e-return-line1',
            source: 'return-bercy',
            target: 'group-line1',
            type: 'animatedFlowCyan',
            sourceHandle: 'right',
            targetHandle: 'left',
        },
        {
            id: 'e-return-line2',
            source: 'return-bercy',
            target: 'group-line2',
            type: 'animatedFlowCyan',
            sourceHandle: 'right',
            targetHandle: 'left',
        },
        // Lines → Pumps (blue flow from right to left)
        {
            id: 'e-line1-pumps',
            source: 'group-line1',
            target: 'pumps-bercy',
            type: 'animatedFlow',
            sourceHandle: 's-right',
            targetHandle: 'left',
        },
        {
            id: 'e-line2-pumps',
            source: 'group-line2',
            target: 'pumps-bercy',
            type: 'animatedFlow',
            sourceHandle: 's-right',
            targetHandle: 'left',
        },
        // Pumps → Departure (blue flow from right to left)
        {
            id: 'e-pumps-departure',
            source: 'pumps-bercy',
            target: 'departure-bercy',
            type: 'animatedFlow',
            sourceHandle: 's-right',
            targetHandle: 'left',
        },
    ];

    return { nodes, edges };
}

// ── CAuber (Aubervilliers) ──────────────────────────────────────────────

function buildCAuber(_groupConfigs?: GroupConfigs): { nodes: Node<IBaseData>[]; edges: Edge[] } {
    const nodes: Node<IBaseData>[] = [
        // Power contract
        {
            id: 'power-auber',
            type: 'contract',
            position: { x: 50, y: 20 },
            data: {
                entityName: 'PowerContract',
                entityLabel: "Contrat d'électricité",
                label: 'Contrat élec AUBERVILLIERS',
            },
            width: 165,
            height: 130,
        },
        // Cooling system
        {
            id: 'cooling-auber',
            type: 'group',
            position: { x: 50, y: 190 },
            data: {
                entityName: 'WaterCooling',
                entityLabel: 'Système de refroidissement',
                label: 'Système de refroidissement AUBER',
            },
            width: 320,
            height: 150,
            style: {
                backgroundColor: 'white',
                border: '1px solid var(--nj-core-color-grey-300)',
                borderRadius: '8px',
            },
        },
        // TAR 1
        {
            id: 'tar-auber-1',
            type: 'asset',
            position: { x: 120, y: 220 },
            data: {
                entityName: 'CoolingTower',
                entityLabel: 'TAR',
                label: 'TAR 1',
            },
            parentId: 'cooling-auber',
            extent: 'parent' as const,
            width: 130,
            height: 90,
        },
        // Return
        {
            id: 'return-auber',
            type: 'asset',
            position: { x: 50, y: 380 },
            data: {
                entityName: 'Return',
                entityLabel: 'Retour',
                label: 'AUBER Retour',
            },
            width: 140,
            height: 120,
        },
        // Production line (2 chillers)
        {
            id: 'group-line-auber',
            type: 'group',
            position: { x: 300, y: 150 },
            data: {
                entityName: 'Group',
                entityLabel: 'Ligne de production',
                label: 'Ligne AUBER',
            },
            width: 380,
            height: 180,
            style: {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                border: '1px solid rgba(25, 118, 210, 0.3)',
                borderRadius: '8px',
            },
        },
        // GF1A
        {
            id: 'chiller-gf1a',
            type: 'asset',
            position: { x: 320, y: 180 },
            data: {
                entityName: 'Chiller',
                entityLabel: 'Groupe froid',
                label: 'GF1A',
            },
            parentId: 'group-line-auber',
            extent: 'parent' as const,
            width: 160,
            height: 130,
        },
        // GF2A
        {
            id: 'chiller-gf2a',
            type: 'asset',
            position: { x: 500, y: 180 },
            data: {
                entityName: 'Chiller',
                entityLabel: 'Groupe froid',
                label: 'GF2A',
            },
            parentId: 'group-line-auber',
            extent: 'parent' as const,
            width: 160,
            height: 130,
        },
        // Pump group (2 pompes)
        {
            id: 'pumps-auber',
            type: 'group',
            position: { x: 750, y: 120 },
            data: {
                entityName: 'Group',
                entityLabel: 'Groupe de pompes',
                label: 'Groupe de pompes AUBER',
            },
            width: 240,
            height: 210,
            style: {
                backgroundColor: 'white',
                border: '1px solid var(--nj-core-color-grey-300)',
                borderRadius: '8px',
            },
        },
        // Pumps
        {
            id: 'pump-a1',
            type: 'asset',
            position: { x: 770, y: 150 },
            data: {
                entityName: 'VariableSpeedPump',
                entityLabel: 'Pompe',
                label: 'Pompe A1',
            },
            parentId: 'pumps-auber',
            extent: 'parent' as const,
            width: 180,
            height: 80,
        },
        {
            id: 'pump-a2',
            type: 'asset',
            position: { x: 770, y: 250 },
            data: {
                entityName: 'VariableSpeedPump',
                entityLabel: 'Pompe',
                label: 'Pompe A2',
            },
            parentId: 'pumps-auber',
            extent: 'parent' as const,
            width: 180,
            height: 80,
        },
        // Departure
        {
            id: 'departure-auber',
            type: 'asset',
            position: { x: 1050, y: 170 },
            data: {
                entityName: 'Departure',
                entityLabel: 'Départ',
                label: 'AUBER Départ',
            },
            width: 140,
            height: 120,
        },
    ];

    const edges: Edge[] = [
        // Power → Cooling (dashed)
        {
            id: 'e-power-cooling-auber',
            source: 'power-auber',
            target: 'cooling-auber',
            style: EDGE_GREY_DASHED,
        },
        // Cooling → Line (orange flow)
        {
            id: 'e-cooling-line-auber',
            source: 'cooling-auber',
            target: 'group-line-auber',
            type: 'animatedFlowOrange',
            sourceHandle: 's-bottom',
            targetHandle: 'top',
        },
        // Return → Line (cyan flow)
        {
            id: 'e-return-line-auber',
            source: 'return-auber',
            target: 'group-line-auber',
            type: 'animatedFlowCyan',
            sourceHandle: 'right',
            targetHandle: 'left',
        },
        // Line → Pumps (blue flow)
        {
            id: 'e-line-pumps-auber',
            source: 'group-line-auber',
            target: 'pumps-auber',
            type: 'animatedFlow',
            sourceHandle: 's-right',
            targetHandle: 'left',
        },
        // Pumps → Departure (blue flow)
        {
            id: 'e-pumps-departure-auber',
            source: 'pumps-auber',
            target: 'departure-auber',
            type: 'animatedFlow',
            sourceHandle: 's-right',
            targetHandle: 'left',
        },
    ];

    return { nodes, edges };
}
